import { MapBoxMode, MapData } from './../../../../../base/shared/map-box-map/map-box-map.component';
import { CreateZoneDeviceDTO, IZoneDeviceParameterConfigDTO, IZoneDeviceParameterDTO, ZoneDTO, ZoneDeviceDirection } from 'src/app/base/models/models.swagger.generated';
import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, AfterViewInit } from '@angular/core';
import {
  UpdateZoneDeviceDTO,
  ZoneDeviceBrand,
  ZoneDeviceCommandDTO,
  ZoneDeviceDTO,
  ZoneDeviceParameterType,
  ZoneDeviceType,
} from 'src/app/base/models/models.swagger.generated';
import { FormBuilder, FormGroup, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { merge, Observable, of } from 'rxjs';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { map, startWith } from 'rxjs/operators';
import { InputType } from 'src/app/base/models/input-type';
import { DropDownItem } from 'src/app/base/shared/drop-down-item.model';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { FormHelper } from 'src/app/base/shared/form-helper';
import { AdministrationItemType, DetailsInitInfo } from '../../data-structures';
import { IProxyUserInfo, ZoneDeviceDetailsService } from './zone-device-details.service';
import { ZoneDeviceAddComponent } from './zone-device-add/zone-device-add.component';
import { TranslateModule } from '@ngx-translate/core';
import { InputFactoryComponent } from '../../../../../base/shared/input-factory/input-factory.component';
import { YesNoGroupButtonComponent } from '../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { NgTemplateOutlet } from '@angular/common';
import { MapBoxMapComponent } from 'src/app/base/shared/map-box-map/map-box-map.component';
import { ZoneDeviceEditFormType } from './zone-device-details-edit-form-type';

interface IZoneDeviceParameterDTOExtended extends IZoneDeviceParameterDTO {
  configuration?: IZoneDeviceParameterConfigDTO;
}

interface IZoneDeviceParameterGroup {
  groupName: string;
  parameters: IZoneDeviceParameterDTOExtended[];
}

@Component({
    selector: 'app-zone-device-details',
    templateUrl: './zone-device-details.component.html',
    styleUrls: ['./zone-device-details.component.scss'],
    imports: [ReactiveFormsModule, NgTemplateOutlet, AppErrorMessagesComponent, DropDownListModule, YesNoGroupButtonComponent, InputFactoryComponent, ZoneDeviceAddComponent, TranslateModule, MapBoxMapComponent]
})
export class ZoneDeviceDetailsComponent implements AfterViewInit, AfterViewChecked {
  @Input() public id: number;
  @Input() public isAdd: boolean;
  @Output() public cancel = new EventEmitter();
  @Output() public deviceUpdated = new EventEmitter<ZoneDeviceDTO>();
  @Output() public deviceCreated = new EventEmitter<ZoneDeviceDTO>();
  @Output() public deviceDeleted = new EventEmitter<number>();
  @Output() public hasPendingDetailChangesChanged = new EventEmitter<boolean>();
  @ViewChild(ZoneDeviceAddComponent) public deviceAddComponent: ZoneDeviceAddComponent;

  @Input() public set detailsInitInfo(value: DetailsInitInfo) {
    const valueStr = value ? JSON.stringify(value) : null;
    const detailsInitInfoStr = this._detailInitInfo ? JSON.stringify(this._detailInitInfo) : null;
    if (valueStr != detailsInitInfoStr) {
      this.zoneDeviceDetailsService.reset();
      this._detailInitInfo = value;
      if (value?.id) {
        this.zoneDeviceDetailsService.getZoneDevice(value.id).subscribe((device: ZoneDeviceDTO) => {
          this.isAdd = false;
          this.selectedDevice = device;
          this.handleDeviceData(this.selectedDevice);
        });
      } else {
        this.deviceAddComponent?.openAdd();
      }
    }
  }

  public get formTitle(): string {
    return !!this.selectedDevice?.id ? 'i18n.domain.administration.zone-device-details.form.title-edit' : 'i18n.domain.administration.zone-device-details.form.title-add';
  };

  public get mapData(): MapData {
    return {
      data: this.deviceForm.controls.coordinates.value,
      mode: MapBoxMode.PositionMode
    };
  }

  public set mapData(value: MapData) {
    this.deviceForm.controls.coordinates.setValue(value.data);
  }

  public get isBusy(): boolean {
    return this.zoneDeviceDetailsService.isBusy;
  }

  public selectedDevice: ZoneDeviceDTO;
  public formReady = false;
  public deviceParameters: IZoneDeviceParameterDTOExtended[];
  public groupedDeviceParameters: IZoneDeviceParameterGroup[] = [];
  public ungroupedDeviceParameters: IZoneDeviceParameterDTOExtended[] = [];
  public readonly UNGROUPED_KEY = '__ungrouped';
  public openSections = new Set<string>();
  public deviceCommands: ZoneDeviceCommandDTO[];
  public deviceParameterSelectOptions = new Map<string, DropDownItem[]>();

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public enumDropdownSettings = DropDownConstantsFactory.createNumericEnumDropdownSettings();
  public proxUserDropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public zones: any[] = [];
  public deviceTypes = this.enumTranslateService.translateNumericEnum(ZoneDeviceType, 'i18n.shared.zone-device-enums.device-type.');
  public deviceBrands = this.enumTranslateService.translateNumericEnum(ZoneDeviceBrand, 'i18n.shared.zone-device-enums.brand.');
  public deviceDirections = this.enumTranslateService.translateNumericEnum(ZoneDeviceDirection, 'i18n.shared.zone-device-enums.direction.');

  public deviceForm: FormGroup<ZoneDeviceEditFormType>;
  public saveButtonDisabled = false;
  public isProxyDriven = false;
  public proxyUsers: IProxyUserInfo[];

  MapBoxMode = MapBoxMode;

  private formInitialValue;
  private _detailInitInfo: DetailsInitInfo;
  private formChanged = false;

  constructor(private fb: FormBuilder,
              private enumTranslateService: EnumTranslationService,
              private cdr: ChangeDetectorRef,
              private modalService: ModalService,
              private zoneDeviceDetailsService: ZoneDeviceDetailsService) {
  }

  public ngAfterViewInit(): void {
    if (!this._detailInitInfo?.id && !this.deviceAddComponent.isAddOpened()) {
      this.deviceAddComponent.openAdd();
    }
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public toggleSection(name: string): void {
    if (this.openSections.has(name)) {
      this.openSections.delete(name);
    } else {
      this.openSections.add(name);
    }
  }

  public isOpen(name: string): boolean {
    return this.openSections.has(name);
  }

  public get sectionCount(): number {
    return (this.ungroupedDeviceParameters.length > 0 ? 1 : 0) + this.groupedDeviceParameters.length;
  }

  public get allSectionsOpen(): boolean {
    return this.sectionCount > 0 && this.openSections.size === this.sectionCount;
  }

  public toggleAllSections(): void {
    if (this.allSectionsOpen) {
      this.openSections.clear();
      return;
    }
    if (this.ungroupedDeviceParameters.length > 0) {
      this.openSections.add(this.UNGROUPED_KEY);
    }
    this.groupedDeviceParameters.forEach(g => this.openSections.add(g.groupName));
  }

  public submitForm(): void {
    this.expandInvalidSections();
    if (this.deviceForm.value.id > 0) {
      this.updateZoneDevice(this.deviceForm);
    } else {
      this.createZoneDevice(this.deviceForm);
    }
  }

  public cancelClicked(): void {
    this.canDeactivate().subscribe(data => {
      if (data) {
        this.closeForm();
        this.cancel.emit();
      }
    });
  }

  public onAddCancelled(): void {
    this.cancel.emit();
  }

  public deleteDevice() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.administration.zone-device-details.delete-confirm.title', 'i18n.domain.administration.zone-device-details.delete-confirm.content');
    dialog.onOkSubject.subscribe(() => {
      this.zoneDeviceDetailsService.deleteZoneDevice(this.selectedDevice.id)
        .subscribe(() => {
          this.deviceDeleted.emit(this.selectedDevice.id);
        });
    });
  }

  public getInputType(paramType: ZoneDeviceParameterType): InputType {
    switch (paramType) {
      case ZoneDeviceParameterType.Phone:
      case ZoneDeviceParameterType.String:
      case ZoneDeviceParameterType.IP:
      case ZoneDeviceParameterType.MacAddress:
        return InputType.Input;
      case ZoneDeviceParameterType.Int:
        return InputType.Integer;
      case ZoneDeviceParameterType.Float:
        return InputType.Float;
      case ZoneDeviceParameterType.Bool:
        return InputType.Bool;
      case ZoneDeviceParameterType.Select:
        return InputType.Select;
      default:
        return InputType.Input;
    }
  }

  public onDeviceStructureCreated(device: ZoneDeviceDTO) {
    this.isAdd = true;
    device.clientId = this._detailInitInfo.clientId;
    device.zone = this._detailInitInfo.parentType === AdministrationItemType.Zone || this._detailInitInfo.parentType === AdministrationItemType.ZoneDeviceCollection ? {id: this._detailInitInfo.parentId} as ZoneDTO : null; // only zone id is needed in the form
    this.selectedDevice = device;
    this.handleDeviceData(this.selectedDevice);
  }

  private handleDeviceData(device: ZoneDeviceDTO) {
    this.isProxyDriven = this.zoneDeviceDetailsService.isDeviceProxyDriven(device.deviceType, device.brand);
    this.formReady = false;
    this.deviceParameters = null;
    this.groupedDeviceParameters = [];
    this.ungroupedDeviceParameters = [];
    this.openSections = new Set<string>();
    this.deviceCommands = null;

    this.initForm(device);

    this.proxyUsers = [];
    if (this.isProxyDriven) {
      this.zoneDeviceDetailsService.getProxyUsers(device.clientId).subscribe((proxyUsers) => {
        this.proxyUsers = proxyUsers;
        if (this.isAdd && this.proxyUsers.length == 1 && this.deviceForm.controls.proxyUserId.value == null) {
          this.deviceForm.controls.proxyUserId.setValue(this.proxyUsers[0].id);
        }
      });
      this.deviceForm.controls.proxyUserId.setValidators(Validators.required);
    } else {
      this.deviceForm.controls.proxyUserId.setValidators(null);
    }

    // handle parameters
    if (!!device.parameters) {
      this.deviceForm.addControl('parameters', new UntypedFormGroup({}));

      this.deviceParameters = device.parameters;
      const groupedParameters = this.groupDeviceParameters(this.deviceParameters);
      this.groupedDeviceParameters = groupedParameters.grouped;
      this.ungroupedDeviceParameters = groupedParameters.ungrouped;

      if (this.ungroupedDeviceParameters.length > 0) {
        this.openSections.add(this.UNGROUPED_KEY);
      } else if (this.groupedDeviceParameters.length > 0) {
        this.openSections.add(this.groupedDeviceParameters[0].groupName);
      }
      device.parameters.forEach(parameter => {
        const param = parameter as any;
        const validators = this.getValidatorsForConfig(param);
        (this.deviceForm.controls.parameters as UntypedFormGroup)
          .addControl(param.configuration.ident.split('.')[1], new UntypedFormControl(param.value ?? param.phone ?? param.selection ?? param.defaultValue ?? '', validators));
        // dots within a form-name kills the connection to it

        if (param.type === ZoneDeviceParameterType.Select) {
          const options = param.configuration.selectionPossibilities.map(value => ({key: value, value}));
          this.deviceParameterSelectOptions.set(param.configuration.ident.split('.')[1], options);
        }
      });
    }

    this.formInitialValue = JSON.parse(JSON.stringify(this.deviceForm.value));
    this.updateFormChangedState(false);
    this.deviceForm.statusChanges
      .pipe(
        startWith(this.deviceForm.status),
      )
      .subscribe(status => {
        const hasChanges = !(JSON.stringify(this.formInitialValue) === JSON.stringify(this.deviceForm.value));
        this.updateFormChangedState(hasChanges);
        this.saveButtonDisabled = (status !== 'VALID') || !hasChanges;
      });

    this.formReady = true;
    FormHelper.updateTreeValidity(this.deviceForm, !this.isAdd);
  }

  private groupDeviceParameters(parameters: IZoneDeviceParameterDTOExtended[]): { grouped: IZoneDeviceParameterGroup[]; ungrouped: IZoneDeviceParameterDTOExtended[] } {
    const groups = new Map<string, IZoneDeviceParameterGroup>();
    const ungrouped: IZoneDeviceParameterDTOExtended[] = [];

    parameters.forEach(parameter => {
      const groupName = parameter.configuration?.group?.groupName?.trim();
      if (!groupName) {
        ungrouped.push(parameter);
        return;
      }

      if (!groups.has(groupName)) {
        groups.set(groupName, {
          groupName,
          parameters: []
        });
      }

      groups.get(groupName).parameters.push(parameter);
    });

    return {
      grouped: Array.from(groups.values()),
      ungrouped
    };
  }

  private getValidatorsForConfig(parameter: any): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const config = parameter.configuration;

    if (config.required) {
      validators.push(Validators.required);
    }

    if (parameter.type === ZoneDeviceParameterType.Int) {
      validators.push(Validators.min(config.minValue));
      validators.push(Validators.max(config.maxValue));
    }
    if (parameter.type === ZoneDeviceParameterType.IP) {
      validators.push(CustomValidatorsFactory.validateIpAddress(config.allowIPV6));
    }
    if (parameter.type === ZoneDeviceParameterType.String) {
      validators.push(Validators.minLength(config.minLength));
      validators.push(Validators.maxLength(config.maxLength));
    }
    if (parameter.type === ZoneDeviceParameterType.Float) {
      validators.push(Validators.min(config.minValue));
      validators.push(Validators.max(config.maxValue));
      validators.push(Validators.pattern('/^-?\d*[.,]?\d{0,' + config.noOfDecimals + '2}$/'));
    }
    if (parameter.type === ZoneDeviceParameterType.Phone) {
      validators.push(CustomValidatorsFactory.createPhoneValidator());
    }
    if (parameter.type === ZoneDeviceParameterType.MacAddress) {
      validators.push(CustomValidatorsFactory.validateMacAddress());
    }

    // no validators (yet)
    // if (parameter.type === ZoneDeviceParameterType.Select) {
    // }
    // if (parameter.type === ZoneDeviceParameterType.Bool) {
    // }

    return validators;
  }

  private initForm(device: ZoneDeviceDTO): void {
    this.deviceForm = null;
    this.deviceForm = this.fb.group<ZoneDeviceEditFormType>({
      id: this.fb.control(device.id),
      name: this.fb.control(device.name, Validators.required),
      deviceType: this.fb.control({value: device.deviceType, disabled: true}),
      brand: this.fb.control({value: device.brand, disabled: true}),
      direction: this.fb.control(device.direction, Validators.required),
      description: this.fb.control(device.description),
      zoneId: this.fb.control(device.zone?.id, Validators.required),
      coordinates: this.fb.control(device.coordinates),
      proxyUserId: this.fb.control(device.proxyUserId),
      enabled: this.fb.control(device.enabled),
    });
    this.cdr.detectChanges();
  }

  private updateZoneDevice(form: FormGroup<ZoneDeviceEditFormType>) {
    const formValues = form.value;
    const dto = new UpdateZoneDeviceDTO({
      id: formValues.id,
      name: formValues.name,
      coordinates: formValues.coordinates,
      description: formValues.description,
      proxyUserId: this.isProxyDriven ? formValues.proxyUserId : null,
      parameters: this.createParameterDTOs(formValues.parameters),
      direction: formValues.direction,
      enabled: formValues.enabled
    });

    this.zoneDeviceDetailsService.updateZoneDevice(dto)
      .subscribe((device) => {
        this.selectedDevice = device;
        this.handleDeviceData(this.selectedDevice);
        this.deviceUpdated.emit(device);
      });
  }

  private createZoneDevice(form: FormGroup<ZoneDeviceEditFormType>) {
    const formValues = form.getRawValue();
    const dto = new CreateZoneDeviceDTO({
      clientId: this.selectedDevice.clientId,
      direction: formValues.direction,
      enabled: formValues.enabled,
      deviceType: formValues.deviceType,
      brand: formValues.brand,
      zoneId: formValues.zoneId,
      name: formValues.name,
      coordinates: formValues.coordinates,
      description: formValues.description,
      proxyUserId: this.isProxyDriven ? formValues.proxyUserId : null,
      parameters: this.createParameterDTOs(formValues.parameters)
    });

    this.zoneDeviceDetailsService.createZoneDevice(dto)
      .subscribe((device) => {
          this.deviceCreated.emit(device);
          this.closeForm();
        });
  }

  private closeForm(): void {
    this.isAdd = true;
    this.selectedDevice = null;
    this.formReady = false;
    this.deviceForm = null;
  }

  private createParameterDTOs(formParameters: any): any[] {
    if (!formParameters) {
      return null;
    }
    const params = [];
    Object.keys(formParameters)
        .forEach(key => {
          const param = this.deviceParameters.find(p => (p as any).configuration.ident.endsWith(key));
          switch(param.type) {
            case ZoneDeviceParameterType.Select:
              (param as any).selection = formParameters[key];
              break;
            case ZoneDeviceParameterType.Phone:
              (param as any).phone = formParameters[key];
              break;
            default:
              (param as any).value = formParameters[key];
              break;
          }

          params.push(param);
        });
    return params;
  }

  private expandInvalidSections(): void {
    if (!this.deviceParameters) return;
    const parametersGroup = this.deviceForm.controls.parameters as UntypedFormGroup;
    if (!parametersGroup) return;

    const hasInvalidParam = (params: IZoneDeviceParameterDTOExtended[]): boolean =>
      params.some(p => {
        const key = (p as any).configuration?.ident?.split('.')[1];
        return key && parametersGroup.get(key)?.invalid;
      });

    if (hasInvalidParam(this.ungroupedDeviceParameters)) {
      this.openSections.add(this.UNGROUPED_KEY);
    }
    this.groupedDeviceParameters.forEach(group => {
      if (hasInvalidParam(group.parameters)) {
        this.openSections.add(group.groupName);
      }
    });
  }

  private canDeactivate(): Observable<boolean> {
    if (this.formChanged) {
      const dialog = this.modalService.showOkCancelDialog('i18n.errors.discard-dialog.title', 'i18n.errors.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => false)),
      );
    }
    return of(true);
  }

  private updateFormChangedState(hasChanges: boolean) {
    if (this.formChanged !== hasChanges) {
      this.formChanged = hasChanges;
      this.hasPendingDetailChangesChanged.emit(hasChanges);
    }
  }
}

